import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import type {ECharts} from 'echarts';
import * as echartsLib from 'echarts';
import {CanvasRenderer, SVGRenderer} from 'echarts/renderers';
import isEqual from 'fast-deep-equal';
import {Box} from '@tempo/core';

import type {
	EchartsEventHandlerUnknown,
	EChartsOption,
	EChartsReactProps,
	EChartsReactRef,
	EventListeners,
	EventNames,
	EventParamWithoutQuery,
	EventParamWithQuery,
} from './types';
import {DefaultEchartsTheme, registerEchartsThemes} from './theme';
import {useColorModeValue} from '@deezer/next/shared/hooks/useColorModeValue';
import {useResizeObserver} from '@deezer/next/shared/hooks/useResizeObserver';

export type {EChartsReactProps, EChartsOption, ECharts, EChartsReactRef};

registerEchartsThemes();
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
echartsLib.use([SVGRenderer, CanvasRenderer]);

type AnyFn = (...args: any[]) => any;

const EChartsReact = forwardRef<EChartsReactRef, EChartsReactProps>(
	(props, ref) => {
		const {
			echarts,
			onEvents,
			onChartReady,
			option,
			notMerge = false,
			lazyUpdate = false,
			replaceMerge,
			showLoading,
			loadingOption = null,
			theme,
			opts = {renderer: 'svg'},
			shouldSetOption,
			...boxProps
		} = props;

		const echartsRef = useRef(echarts || echartsLib);
		const instanceRef = useRef<ECharts | undefined>(undefined);
		const eleRef = useRef<HTMLDivElement>(null!);
		const isInitialResizeRef = useRef(true);

		const defaultTheme = useColorModeValue(
			DefaultEchartsTheme.LIGHT,
			DefaultEchartsTheme.DARK,
		);

		const activeTheme = useMemo(
			() => theme || defaultTheme,
			[defaultTheme, theme],
		);

		useImperativeHandle(
			ref,
			() => ({
				getEchartsInstance: () => {
					if (!eleRef.current) return undefined;
					return (
						instanceRef.current ||
						echartsRef.current.getInstanceByDom(eleRef.current)
					);
				},
			}),
			[],
		);

		const resize = useCallback(() => {
			if (instanceRef.current && !isInitialResizeRef.current) {
				try {
					instanceRef.current.resize();
				} catch {
					// no-op
				}
			}
			isInitialResizeRef.current = false;
		}, []);

		useResizeObserver(eleRef, resize);

		const dispose = useCallback(() => {
			if (eleRef.current && instanceRef.current) {
				echartsRef.current.dispose(eleRef.current);
				instanceRef.current = undefined;
			}
		}, []);

		useEffect(() => {
			return () => dispose();
		}, [dispose]);

		const boundEventsRef = useRef<EventListeners | undefined>(undefined);

		const unbindEvents = useCallback(() => {
			if (!instanceRef.current || !boundEventsRef.current) return;

			Object.entries(boundEventsRef.current).forEach(
				([eventName, handlerParam]) => {
					if (typeof handlerParam === 'function') {
						instanceRef.current?.off(
							eventName,
							handlerParam as EchartsEventHandlerUnknown,
						);
					} else {
						instanceRef.current?.off(eventName, handlerParam.handler as AnyFn);
					}
				},
			);

			boundEventsRef.current = undefined;
		}, []);

		const bindEvents = useCallback(() => {
			if (!instanceRef.current || !onEvents) return;

			const wrapped = Object.entries(onEvents).reduce<EventListeners>(
				(acc, [eventName, handlerParam]) => {
					if (typeof handlerParam === 'function') {
						acc[eventName as EventNames] = (event: any) =>
							(handlerParam as AnyFn)(event, instanceRef.current);
					} else {
						const hp = handlerParam as
							EventParamWithQuery | EventParamWithoutQuery;
						acc[eventName as EventNames] = {
							...hp,
							handler: ((event: any) =>
								hp.handler(event, instanceRef.current)) as AnyFn,
						} as any;
					}
					return acc;
				},
				{} as EventListeners,
			);

			Object.entries(wrapped).forEach(([eventName, handlerParam]) => {
				if (typeof handlerParam === 'function') {
					instanceRef.current?.on(
						eventName,
						handlerParam as EchartsEventHandlerUnknown,
					);
				} else {
					const hp = handlerParam as
						EventParamWithQuery | EventParamWithoutQuery;
					if ((hp as EventParamWithQuery).query) {
						const withQuery = hp as EventParamWithQuery;
						instanceRef.current?.on(
							eventName,
							withQuery.query,
							withQuery.handler as EchartsEventHandlerUnknown,
							withQuery.context,
						);
					} else {
						const withoutQuery = hp as EventParamWithoutQuery;
						instanceRef.current?.on(
							eventName,
							withoutQuery.handler as EchartsEventHandlerUnknown,
							withoutQuery.context,
						);
					}
				}
			});

			boundEventsRef.current = wrapped;
		}, [onEvents]);

		const updateEChartsOption = useCallback(() => {
			if (!instanceRef.current) return;

			instanceRef.current.setOption(option, {
				notMerge,
				lazyUpdate,
				replaceMerge,
			});

			if (showLoading) {
				instanceRef.current.showLoading(loadingOption);
			} else {
				instanceRef.current.hideLoading();
			}
		}, [
			lazyUpdate,
			loadingOption,
			notMerge,
			option,
			replaceMerge,
			showLoading,
		]);

		const initChart = useCallback(() => {
			if (!eleRef.current) return;

			const existing = echartsRef.current.getInstanceByDom(eleRef.current);

			if (!instanceRef.current || !existing) {
				instanceRef.current = echartsRef.current.init(
					eleRef.current,
					activeTheme,
					opts,
				);
				isInitialResizeRef.current = true;
			}

			if (!instanceRef.current) return;

			updateEChartsOption();

			unbindEvents();
			bindEvents();

			onChartReady?.(instanceRef.current);
		}, [
			activeTheme,
			bindEvents,
			onChartReady,
			opts,
			unbindEvents,
			updateEChartsOption,
		]);

		useEffect(() => {
			if (!instanceRef.current) {
				initChart();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const prevThemeRef = useRef(activeTheme);
		const prevOptsRef = useRef(opts);

		useEffect(() => {
			const themeChanged = !isEqual(prevThemeRef.current, activeTheme);
			const optsChanged = !isEqual(prevOptsRef.current, opts);

			if (!themeChanged && !optsChanged) {
				return;
			}

			prevThemeRef.current = activeTheme;
			prevOptsRef.current = opts;

			unbindEvents();
			dispose();
			initChart();
		}, [activeTheme, dispose, initChart, opts, unbindEvents]);

		useEffect(() => {
			if (!instanceRef.current) return;

			unbindEvents();
			bindEvents();

			return () => {
				unbindEvents();
			};
		}, [bindEvents, unbindEvents]);

		const prevPropsRef = useRef<EChartsReactProps | undefined>(undefined);

		useEffect(() => {
			if (!instanceRef.current) return;

			const prev = prevPropsRef.current;
			prevPropsRef.current = props;

			if (prev && shouldSetOption && !shouldSetOption(prev, props)) {
				return;
			}

			const shouldUpdate =
				!prev ||
				!isEqual(prev.option, option) ||
				!isEqual(prev.notMerge, notMerge) ||
				!isEqual(prev.lazyUpdate, lazyUpdate) ||
				!isEqual(prev.replaceMerge, replaceMerge) ||
				!isEqual(prev.showLoading, showLoading) ||
				!isEqual(prev.loadingOption, loadingOption);

			if (shouldUpdate) {
				updateEChartsOption();
			}
		}, [
			lazyUpdate,
			loadingOption,
			notMerge,
			option,
			props,
			replaceMerge,
			shouldSetOption,
			showLoading,
			updateEChartsOption,
		]);

		return <Box ref={eleRef} height={300} {...boxProps} />;
	},
);

EChartsReact.displayName = 'EChartsReact';

export default EChartsReact;
